import api from "./apiWithAccessToken";
import {
  DEMO_DASHBOARD_LIST_ENTRY,
  getDemoSavedDashboard,
  isDemoDashboardId,
} from "./components/DashboardAgent/dashboardDemo";

const DASHBOARD_BASE_PATH = "/dashboard";

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallbackMessage;

const normalizeApiError = (error, fallbackMessage) => {
  const normalized = new Error(getErrorMessage(error, fallbackMessage));
  normalized.code = error?.response?.data?.code;
  normalized.provider = error?.response?.data?.provider;
  normalized.status = error?.response?.status;
  return normalized;
};

const sortDashboardsByUpdatedAt = (dashboards) =>
  [...dashboards].sort((left, right) => {
    const leftTime = new Date(left.updated_at || left.created_at || 0).getTime();
    const rightTime = new Date(right.updated_at || right.created_at || 0).getTime();
    return rightTime - leftTime;
  });

const withPinnedDemoDashboard = (dashboards) => {
  const normalizedDashboards = Array.isArray(dashboards) ? dashboards : [];
  const withoutDemo = normalizedDashboards.filter((dashboard) => !isDemoDashboardId(dashboard?.id));
  return [DEMO_DASHBOARD_LIST_ENTRY, ...sortDashboardsByUpdatedAt(withoutDemo)];
};

const normalizeGeneratedResponse = (response, savedDashboard) => {
  if (!savedDashboard) return response;
  return {
    ...response,
    saved_dashboard_id: savedDashboard.id,
    created_at: savedDashboard.created_at,
    updated_at: savedDashboard.updated_at,
  };
};

export const listSavedDashboards = async ({ sourceDbId } = {}) => {
  try {
    const { data } = await api.get(DASHBOARD_BASE_PATH);
    const dashboards = Array.isArray(data?.dashboards) ? data.dashboards : [];
    const filteredDashboards =
      sourceDbId === undefined || sourceDbId === null
        ? dashboards
        : dashboards.filter((dashboard) => String(dashboard.source_db_config_id) === String(sourceDbId));

    return {
      dashboards: withPinnedDemoDashboard(filteredDashboards),
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to load dashboards."));
  }
};

export const getSavedDashboard = async (dashboardId) => {
  if (isDemoDashboardId(dashboardId)) {
    return {
      saved_dashboard: getDemoSavedDashboard(),
    };
  }

  try {
    const { data } = await api.get(`${DASHBOARD_BASE_PATH}/${dashboardId}`);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Saved dashboard not found."));
  }
};

export const generateDashboard = async ({ question, source_db_config_id }) => {
  try {
    const { data } = await api.post(`${DASHBOARD_BASE_PATH}/generate`, {
      question,
      source_db_config_id,
    });

    if (!data?.dashboard_response_id) {
      return data;
    }

    try {
      const saveResponse = await api.post(`${DASHBOARD_BASE_PATH}/save`, {
        dashboard_response_id: data.dashboard_response_id,
        title: data?.dashboard?.metadata?.title,
      });
      return normalizeGeneratedResponse(data, saveResponse?.data?.saved_dashboard);
    } catch (saveError) {
      return {
        ...data,
        save_error: getErrorMessage(saveError, "Dashboard generated but could not be saved."),
      };
    }
  } catch (error) {
    throw normalizeApiError(error, "Unable to generate dashboard.");
  }
};

export const refreshSavedDashboard = async (dashboardId) => {
  try {
    const { data } = await api.post(`${DASHBOARD_BASE_PATH}/${dashboardId}/refresh`);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to refresh dashboard."));
  }
};

export const deleteSavedDashboard = async (dashboardId) => {
  try {
    const { data } = await api.delete(`${DASHBOARD_BASE_PATH}/${dashboardId}`);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to delete dashboard."));
  }
};

export const renameSavedDashboard = async (dashboardId, title) => {
  try {
    const { data } = await api.patch(`${DASHBOARD_BASE_PATH}/${dashboardId}`, { title });
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Failed to rename dashboard."));
  }
};
